import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loading } from '@carbon/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  keycloak,
  useAccessStore,
  KeycloakContext,
  KeycloakUserInfo,
} from '@sphere/auth/data-access';
import type { KeycloakTokenParsed } from 'keycloak-js';
import { clearAppCache, SESSION_CHANGED_FLAG } from '@sphere/shared/util';

interface KeycloakProviderProps {
  children: React.ReactNode;
  landingPath?: string; // ex: '/fr_fr'
}

/**
 * Event interne pour découpler l'app du client HTTP.
 * detail: { ready: boolean; authenticated: boolean }
 */
const notifyAuthReady = (ready: boolean, authenticated: boolean) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('sphere:auth-ready', { detail: { ready, authenticated } })
  );
};

type AuthState = {
  token: string | null;
  tokenParsed: KeycloakTokenParsed | null;
  authenticated: boolean;
  initialized: boolean;
  isLoading: boolean;
  userInfo: KeycloakUserInfo | null;
};

const initialState: AuthState = {
  token: null,
  tokenParsed: null,
  authenticated: false,
  initialized: false,
  isLoading: true,
  userInfo: null,
};

const hasOAuthParamsInUrl = () => {
  const s = window.location.search;
  const h = window.location.hash;
  return (
    s.includes('code=') ||
    s.includes('state=') ||
    h.includes('code=') ||
    h.includes('state=')
  );
};

const cleanupOAuthParamsFromUrl = () => {
  // Cas HashRouter / fragments Keycloak
  if (
    window.location.hash.includes('state=') ||
    window.location.hash.includes('code=')
  ) {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search
    );
  }
  // Cas query string (si ton realm le renvoie en search)
  if (
    window.location.search.includes('state=') ||
    window.location.search.includes('code=')
  ) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};

// ✅ OPTIMISATION : Cache du statut d'authentification pour redirection ultra-rapide
const AUTH_CACHE_KEY = 'sphere:auth:cache';
const AUTH_CACHE_TTL = 30000; // 30 secondes

const getAuthCache = () => {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    if (!cached) return null;
    const { authenticated, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > AUTH_CACHE_TTL) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
    return authenticated;
  } catch {
    return null;
  }
};

const setAuthCache = (authenticated: boolean) => {
  try {
    localStorage.setItem(
      AUTH_CACHE_KEY,
      JSON.stringify({ authenticated, timestamp: Date.now() })
    );
  } catch {
    // Ignore errors (private browsing, quota exceeded, etc.)
  }
};

// ✅ OPTIMISATION : Cookie de session partagé pour Edge Middleware de la landing
// Ce cookie est créé sur .sphere-education.com pour être accessible par la landing
const setSharedAuthCookie = (authenticated: boolean) => {
  if (typeof document === 'undefined') return;

  const cookieName = 'sphere-auth-session';
  const domain = '.sphere-education.com'; // Partagé entre landing et app
  const maxAge = 30; // 30 secondes (même TTL que le cache localStorage)

  if (authenticated) {
    // Créer le cookie avec domain partagé
    document.cookie = `${cookieName}=1; domain=${domain}; path=/; max-age=${maxAge}; secure; samesite=lax`;
    console.log(`[Auth] Shared cookie created for domain ${domain}`);
  } else {
    // Supprimer le cookie
    document.cookie = `${cookieName}=; domain=${domain}; path=/; max-age=0`;
    console.log(`[Auth] Shared cookie removed`);
  }
};

export const KeycloakProvider = ({
  children,
  landingPath = '/fr_fr',
}: KeycloakProviderProps) => {
  const queryClient = useQueryClient();

  // ⚠️ Idéalement, typé : useAccessStore((s: AccessStore) => s.clear)
  const clearAccessStore = useAccessStore((state: any) => state.clear);

  const [state, setState] = useState<AuthState>(initialState);

  // Anti double init (StrictMode)
  const initStarted = useRef(false);

  // ✅ OPTIMISATION : Précharger Keycloak en background
  // Preconnect établit la connexion complète (DNS + TCP + TLS) avant l'init
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const keycloakUrl =
        import.meta.env.VITE_KEYCLOAK_URL ||
        'https://keycloak.sphere-education.com';

      // Preconnect pour connexion complète (plus rapide que dns-prefetch)
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = keycloakUrl;
      preconnect.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect);

      // DNS prefetch comme fallback pour navigateurs anciens
      const dnsPrefetch = document.createElement('link');
      dnsPrefetch.rel = 'dns-prefetch';
      dnsPrefetch.href = keycloakUrl;
      document.head.appendChild(dnsPrefetch);

      // Précharger la page silent-check-sso.html pour qu'elle soit en cache
      const prefetchSilent = document.createElement('link');
      prefetchSilent.rel = 'prefetch';
      prefetchSilent.href = '/silent-check-sso.html';
      prefetchSilent.as = 'document';
      document.head.appendChild(prefetchSilent);
    }
  }, []);

  // Heartbeat timer
  const heartbeatRef = useRef<number | null>(null);

  // Anti refresh concurrent
  const refreshingRef = useRef(false);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const safeUpdateToken = useCallback(async (minValidity: number) => {
    if (refreshingRef.current) return false;
    refreshingRef.current = true;
    try {
      return await keycloak.updateToken(minValidity);
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  const hardResetAppAuth = useCallback(() => {
    stopHeartbeat();
    clearAppCache();
    clearAccessStore();
    queryClient.clear();

    setState({
      token: null,
      tokenParsed: null,
      authenticated: false,
      initialized: true,
      isLoading: false,
      userInfo: null,
    });

    notifyAuthReady(true, false);
  }, [clearAccessStore, queryClient, stopHeartbeat]);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();

    heartbeatRef.current = window.setInterval(() => {
      safeUpdateToken(70)
        .then((refreshed) => {
          if (refreshed && keycloak.token) {
            setState((prev) => ({
              ...prev,
              token: keycloak.token!,
              tokenParsed:
                (keycloak.tokenParsed as KeycloakTokenParsed) ?? null,
            }));
          }
        })
        .catch((err) => {
          console.error('[Auth] Heartbeat refresh error:', err);
          // Session expirée => nettoyer local et re-login automatique
          stopHeartbeat();
          clearAppCache();
          clearAccessStore();
          queryClient.clear();
          // Re-login automatique via SSO (si session SSO valide, pas de saisie credentials)
          keycloak.login({ redirectUri: window.location.href });
        });
    }, 60_000);
  }, [hardResetAppAuth, safeUpdateToken, stopHeartbeat]);

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    const run = async () => {
      try {
        const currentPath = window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);

        const isDevelopment =
          import.meta.env.DEV || import.meta.env.MODE === 'development';

        const loginIntent = searchParams.has('login'); // ex: ?login=true
        const oauthBack = hasOAuthParamsInUrl();

        // Si on est sur la landing et que le cache indique qu'on est authentifié
        // => Rediriger immédiatement vers l'app (sans attendre Keycloak)
        const cachedAuth = getAuthCache();
        const isKnownAppHost =
          window.location.hostname.includes('app.sphere-education.com') ||
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.startsWith('192.168.') ||
          isDevelopment;
        const isOnLanding = !isKnownAppHost;

        if (cachedAuth === true && isOnLanding && !oauthBack && !loginIntent) {
          console.log(
            '[Auth] Cached auth detected, optimistic redirect to app'
          );
          window.location.replace('https://app.sphere-education.com');
          return;
        }

        // Mode de chargement Keycloak
        // Si on a des paramètres OAuth, ne pas spécifier onLoad pour laisser Keycloak gérer
        let onLoad: 'login-required' | 'check-sso' | undefined;
        if (oauthBack) {
          onLoad = undefined; // Keycloak détectera automatiquement les params OAuth
        } else if (loginIntent) {
          onLoad = 'login-required';
        } else {
          onLoad = 'check-sso';
        }

        console.log(
          '[Auth] Init with onLoad:',
          onLoad,
          'oauthBack:',
          oauthBack
        );

        const authenticated = await keycloak.init({
          onLoad,
          pkceMethod: 'S256',
          // ✅ OPTIMISATION : Utiliser Silent Check SSO au lieu de l'iframe polling
          // Plus rapide et moderne - pas de polling, juste une vérification initiale
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          checkLoginIframe: false, // Désactiver l'iframe polling (moins performant)
          messageReceiveTimeout: 2000, // Timeout agressif : 2s max (fail-fast)
          enableLogging: isDevelopment, // Active le logging en dev pour débugger
        });

        // --- Non authentifié ---
        if (!authenticated) {
          // ✅ OPTIMISATION : Mettre à jour le cache (non authentifié)
          setAuthCache(false);
          setSharedAuthCookie(false);

          // ⚠️ Si on revient de Keycloak avec les params OAuth mais pas authentifié
          // => Erreur d'authentification (code expiré, state invalide, etc.)
          if (oauthBack) {
            console.error(
              '[Auth] OAuth callback failed. Code expired or invalid. Redirecting to landing...'
            );
            hardResetAppAuth();
            // Rediriger vers la landing avec un paramètre d'erreur
            window.location.replace(
              'https://sphere-education.com?auth_error=true'
            );
            return;
          }

          // En DEV: force le login automatique
          if (isDevelopment && !loginIntent) {
            await keycloak.login({ redirectUri: window.location.origin });
            return;
          }

          // PROD: Si on est sur l'app, rediriger vers la landing
          const isOnApp = window.location.hostname.includes(
            'app.sphere-education.com'
          );

          if (isOnApp) {
            window.location.replace('https://sphere-education.com');
            return;
          }

          // Si on est déjà sur la landing, rester là
          // Le bouton "Connexion" déclenchera keycloak.login()
          setState((prev) => ({
            ...prev,
            authenticated: false,
            initialized: true,
            isLoading: false,
            token: null,
            tokenParsed: null,
            userInfo: null,
          }));
          notifyAuthReady(true, false);
          return;
        }

        // --- Authentifié ---
        if (keycloak.token) {
          // ✅ OPTIMISATION : Mettre à jour le cache (authentifié)
          setAuthCache(true);
          setSharedAuthCookie(true);

          // Si on est sur la landing ET authentifié, rediriger vers l'app
          if (!isKnownAppHost) {
            window.location.replace('https://app.sphere-education.com');
            return;
          }

          // Nettoyage complet avant chargement du profil (évite mélange de comptes)
          clearAppCache();

          const userInfo = await keycloak.loadUserInfo();
          const enrichedUserInfo: KeycloakUserInfo = {
            ...userInfo,
            realmRoles: keycloak.tokenParsed?.realm_access?.roles,
          };

          // Nettoyage URL
          cleanupOAuthParamsFromUrl();

          // Gérer la redirection après login
          const redirectParam = searchParams.get('redirect');
          if (redirectParam) {
            window.history.replaceState({}, document.title, redirectParam);
          } else if (
            loginIntent ||
            window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/auth')
          ) {
            window.history.replaceState({}, document.title, '/');
          }

          setState({
            token: keycloak.token,
            tokenParsed: (keycloak.tokenParsed as KeycloakTokenParsed) ?? null,
            authenticated: true,
            initialized: true,
            isLoading: false,
            userInfo: enrichedUserInfo,
          });

          notifyAuthReady(true, true);

          // Heartbeat + handler d’expiration
          startHeartbeat();

          keycloak.onTokenExpired = () => {
            safeUpdateToken(30)
              .then((refreshed) => {
                if (refreshed && keycloak.token) {
                  setState((prev) => ({
                    ...prev,
                    token: keycloak.token!,
                    tokenParsed:
                      (keycloak.tokenParsed as KeycloakTokenParsed) ?? null,
                  }));
                }
              })
              .catch(() => {
                // Expiré + non rafraîchissable => re-login automatique
                stopHeartbeat();
                clearAppCache();
                clearAccessStore();
                queryClient.clear();
                keycloak.login({ redirectUri: window.location.href });
              });
          };

          return;
        }

        // Authentifié sans token (rare) => reset local
        hardResetAppAuth();
      } catch (e) {
        console.error('[Auth] Keycloak init error:', e);
        hardResetAppAuth();
        // Option: ne pas relancer login automatiquement pour éviter storm
        // keycloak.login({ redirectUri: window.location.origin });
      }
    };

    run();

    return () => {
      stopHeartbeat();
      keycloak.onTokenExpired = undefined;
    };
  }, [
    hardResetAppAuth,
    landingPath,
    safeUpdateToken,
    startHeartbeat,
    stopHeartbeat,
  ]);

  const logout = useCallback(() => {
    // Marquer qu'il faut nettoyer au prochain démarrage
    sessionStorage.setItem(SESSION_CHANGED_FLAG, 'true');

    // ✅ OPTIMISATION : Nettoyer le cache d'authentification
    setAuthCache(false);
    setSharedAuthCookie(false);

    // Notifier l'app / client HTTP
    notifyAuthReady(false, false);

    // Nettoyage local complet
    stopHeartbeat();
    clearAppCache();
    clearAccessStore();
    queryClient.clear();

    // Déconnexion IdP
    keycloak.logout({ redirectUri: window.location.origin });
  }, [clearAccessStore, queryClient, stopHeartbeat]);

  const contextValue = useMemo(
    () => ({
      token: state.token,
      tokenParsed: state.tokenParsed,
      authenticated: state.authenticated,
      initialized: state.initialized,
      userInfo: state.userInfo,
      keycloakInstance: keycloak,
      logout,
    }),
    [
      logout,
      state.authenticated,
      state.initialized,
      state.token,
      state.tokenParsed,
      state.userInfo,
    ]
  );

  if (state.isLoading) {
    return (
      <Loading
        description="Initialisation de l'authentification..."
        withOverlay
      />
    );
  }

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
};
